import { useEffect, useMemo, useState } from 'react';
import { notifyError } from '../bugsnag';

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
function checkStatus(response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  // TODO: ensure backend renders 404 when studyId/locale is missing
  // https://app.bugsnag.com/trialbee/self-assessment/errors/6475360bfecaf70008d4989f?event_id=65a50b4600d0018894110000&i=wh&m=nw
  const error = new Error(response.statusText || `Status: ${response.status}`);
  error.response = response;
  throw error;
}

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
function parseJSON(response) {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response.json();
}

async function makeRequest({ config, method, url, body, sessionToken }) {
  let receivedToken = sessionToken;
  try {
    const headers = {
      'Content-Type': 'application/json',
    };
    if (sessionToken) {
      headers['TB-Session-Token'] = sessionToken;
    }

    const res = await fetch(`${config.baseURI}${url}`, {
      method,
      credentials: 'include',
      mode: 'cors',
      body: body ? JSON.stringify(body) : undefined,
      headers: new Headers(headers),
    });
    checkStatus(res);
    const json = await parseJSON(res);

    /**
     * HACK: This is stored mainly because of Safari handling cookies in an iframe poorly. See more in
     * the `osa` rest endpoint in external-api-service.
     */
    if (res.headers.has('TB-Session-Token')) {
      receivedToken = res.headers.get('TB-Session-Token');
    }

    return [json, receivedToken];
  } catch (error) {
    notifyError({ error, body });

    let errorCode = 'unknownError';
    if (error instanceof Error) {
      errorCode = error;
    } else if (error instanceof Object) {
      ({ errorCode } = error);
    }
    return [null, receivedToken, errorCode];
  }
}

export function useRequest(request, ...params) {
  const [result, setResult] = useState(null);

  useEffect(() => {
    request(...params).then((res) => setResult(res));
  }, [...params]);

  return [result === null, result];
}

export function useAPI(config, sessionToken) {
  const [error, setError] = useState(null);

  const api = useMemo(() => {
    async function _makeRequest(method, url, body) {
      const [json, _, errorCode] = await makeRequest({
        config,
        method,
        url,
        body,
        sessionToken,
      });
      setError(errorCode);
      return json;
    }

    return {
      submitQuestion(id, answer) {
        return _makeRequest('PUT', `/question/${id}`, { answer });
      },

      getQuestionnaireResults() {
        return _makeRequest('GET', '/questionnaireResults');
      },

      submitSite({ id, studyId }) {
        return _makeRequest('PUT', '/site', { siteId: id, studyId });
      },

      submitContactDetails(contactDetails) {
        return _makeRequest('PUT', '/contactDetails', contactDetails);
      },

      dropoutCandidate() {
        return _makeRequest('PUT', '/dropout');
      },

      finalizeSession() {
        return _makeRequest('POST', '/finalize');
      },
    };
  }, [config.baseURI, config.studyId, config.locale, sessionToken]);

  return [api, error];
}

export async function initSession(config) {
  const { studyId, ...body } = config;
  const [json, receivedToken, errorCode] = await makeRequest({
    config,
    method: 'POST',
    url: `/init/${studyId}`,
    body,
  });
  if (errorCode) {
    throw String(errorCode);
  }
  return [json, receivedToken];
}
